import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Tabs,
  Upload,
} from "antd";
import {
  CheckCircleTwoTone,
  CheckSquareOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  SaveOutlined,
  ScheduleOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import ChecklistItemsEditor from "../checklists/ChecklistItemsEditor";
import { validateChecklistItems } from "../checklists/checklistValidation";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../helper/googleMaps";
import "./CreateTaskModal.css";

const DEFAULT_MAP_CENTER = { lat: 40.7608, lng: -111.891 }; // Salt Lake City, UT

// IANA timezones a property can run in (US coverage). Shared label/value list so
// the form and any display stay consistent.
export const PROPERTY_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];

// Maps each form field to the tab that holds it, so a failed submit can jump
// straight to the section with the first error instead of leaving the user on
// a tab that looks valid.
const FIELD_TO_TAB = {
  propertyName: "property",
  beds: "property",
  bath: "property",
  kitchen: "property",
  porch: "property",
  lawn: "property",
  propertyDescription: "property",
  location: "location",
  ownerName: "owner",
  ownerContact: "owner",
  ownerEmail: "owner",
  assignedInspectors: "scheduling",
  inspectionIntervalWeeks: "scheduling",
  timezone: "scheduling",
};

// Step order for the create wizard. The Tabs render in this order too, so the
// tab strip doubles as the progress indicator.
const TAB_ORDER = ["property", "location", "owner", "scheduling", "checklist"];

// Inverse of FIELD_TO_TAB: the form fields each step owns, so the create
// wizard's "Continue" can validate just the current step before advancing.
// (checklist has no Form.Item fields — its items are validated on final submit.)
const TAB_TO_FIELDS = Object.entries(FIELD_TO_TAB).reduce(
  (acc, [field, tab]) => {
    (acc[tab] = acc[tab] || []).push(field);
    return acc;
  },
  {},
);

// Small section intro line used at the top of each tab for orientation.
const SectionIntro = ({ children }) => (
  <p style={{ margin: "0 0 16px", color: "#8c8378", fontSize: 13 }}>{children}</p>
);

const CreateTaskModal = ({
  open,
  editingTask,
  onCancel,
  form,
  onValuesChange,
  onFinish,
  onFinishFailed,
  statusOptions,
  bedOptions,
  bathOptions,
  kitchenOptions,
  porchOptions,
  lawnOptions,
  usersData,
  onUserChange,
  uploadProps,
  fileList,
  questionaire,
  setQuestionaire,
  coordinates,
  setCoordinates,
  handleAddQuestion,
  handleAddProblem,
  handleAddResolutionStep,
  handleRemoveResolutionStep,
  templates = [],
  onApplyTemplate,
  onSaveAsTemplate,
  isloading,
  onCloseAndReset,
}) => {
  // Load the Google Maps JS SDK via the shared loader options (same id +
  // libraries everywhere) so the map and the Places autocomplete can both use
  // it without tripping the "Loader must not be called again with different
  // options" singleton error.
  const { isLoaded: isMapsLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [activeTab, setActiveTab] = useState("property");
  // Steps the user has passed validation on (create wizard only). Drives the
  // green ✓ in the tab strip. Keyed by tab key; a step lands here once its
  // "Continue" validation succeeds.
  const [completedTabs, setCompletedTabs] = useState({});

  // Each time the modal opens, start on the first tab (and clear progress) so
  // create/edit always begins at a predictable place.
  useEffect(() => {
    if (open) {
      setActiveTab("property");
      setCompletedTabs({});
    }
  }, [open]);

  // Places autocomplete bound to the Ant Design Input's underlying <input> DOM
  // node. We attach it manually (instead of react-google-autocomplete) because
  // the input lives inside a `destroyOnClose` Modal — it doesn't exist in the
  // DOM until the modal opens, so binding has to wait for BOTH the Maps SDK and
  // the open input. When a place is chosen we push the formatted address into
  // the `location` form field and capture lat/lng into `coordinates` state.
  const locationInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!isMapsLoaded || !open) return undefined;

    let cancelled = false;
    let timerId;
    let autocomplete;
    let listener;

    // The Modal mounts its <input> asynchronously (and re-mounts it on each open
    // because of destroyOnClose), so poll briefly until the DOM node and the
    // Places SDK are both ready, then bind the Autocomplete.
    const tryBind = (attempt = 0) => {
      if (cancelled || autocompleteRef.current) return;
      const input = locationInputRef.current;
      const placesNs = window.google?.maps?.places;
      if (!input || !placesNs?.Autocomplete) {
        if (attempt < 25) {
          timerId = setTimeout(() => tryBind(attempt + 1), 120);
        }
        return;
      }

      autocomplete = new placesNs.Autocomplete(input, {
        types: ["address"],
        fields: ["formatted_address", "geometry", "name"],
      });
      autocompleteRef.current = autocomplete;

      listener = autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const formatted = place?.formatted_address || place?.name || "";
        if (formatted) {
          form.setFieldsValue({ location: formatted });
        }
        // Keep the address text and the map pin in sync: set coordinates only
        // for a real suggestion (one with geometry). Pressing Enter without
        // picking a suggestion yields no geometry, so clear any stale pin from
        // a previous selection rather than leaving address/coords mismatched.
        const loc = place?.geometry?.location;
        setCoordinates(loc ? { lat: loc.lat(), lng: loc.lng() } : null);
      });
    };
    tryBind();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      listener?.remove?.();
      if (autocomplete) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocomplete);
      }
      // Google appends the suggestions dropdown to <body> and never removes it;
      // drop any orphaned containers so they don't accumulate across open/close.
      document.querySelectorAll(".pac-container").forEach((el) => el.remove());
      autocompleteRef.current = null;
    };
  }, [isMapsLoaded, open, form, setCoordinates]);

  const handleMarkerDragEnd = (e) => {
    if (!e?.latLng) return;
    setCoordinates({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const mapCenter = coordinates || DEFAULT_MAP_CENTER;

  const imageOnlyBeforeUpload = (file) => {
    const isImage = file?.type?.startsWith("image/");
    if (!isImage) {
      message.error("Only image files are allowed");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const isEditing = Boolean(editingTask?._id);
  const modalTitle = isEditing
    ? `Editing Property${
        editingTask?.propertyName ? ` - ${editingTask.propertyName}` : ""
      }`
    : "Create Property";

  // On a failed submit, jump to the tab that owns the first invalid field so the
  // error is actually visible, then defer to the parent's handler (toast +
  // scroll-to-field).
  const handleFinishFailed = (errorInfo) => {
    const firstField = errorInfo?.errorFields?.[0]?.name?.[0];
    const tab = FIELD_TO_TAB[firstField];
    if (tab) setActiveTab(tab);
    onFinishFailed?.(errorInfo);
  };

  // Create wizard navigation. "Continue" validates ONLY the current step's
  // fields (so problems surface inline, step by step) and advances only when
  // they pass; "Back" steps to the previous tab. Tabs stay clickable, so this
  // is a guided path rather than a hard lock. Editing skips the wizard entirely.
  const currentIndex = TAB_ORDER.indexOf(activeTab);
  const isLastTab = currentIndex === TAB_ORDER.length - 1;

  const handleContinue = async () => {
    const fields = TAB_TO_FIELDS[activeTab];
    try {
      if (fields?.length) await form.validateFields(fields);
      setCompletedTabs((prev) => ({ ...prev, [activeTab]: true }));
      if (currentIndex < TAB_ORDER.length - 1) {
        setActiveTab(TAB_ORDER[currentIndex + 1]);
      }
    } catch (errorInfo) {
      // validateFields rejects with the invalid fields AND renders their inline
      // errors; just bring the first one into view and stay on this step.
      const firstField = errorInfo?.errorFields?.[0]?.name;
      if (firstField) form.scrollToField(firstField);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setActiveTab(TAB_ORDER[currentIndex - 1]);
  };

  // Editing a field on a step that already earned its ✓ drops that step back to
  // "incomplete" — it must re-pass validation (via Continue) to mark done again,
  // so the checkmark never lies after a backward edit. Defers to the parent's
  // onValuesChange (draft autosave) afterwards.
  const handleValuesChange = (changedValues, allValues) => {
    const touchedTabs = Object.keys(changedValues)
      .map((field) => FIELD_TO_TAB[field])
      .filter(Boolean);
    if (touchedTabs.length) {
      setCompletedTabs((prev) => {
        let next = prev;
        for (const tab of touchedTabs) {
          if (next[tab]) {
            if (next === prev) next = { ...prev };
            delete next[tab];
          }
        }
        return next;
      });
    }
    onValuesChange?.(changedValues, allValues);
  };

  // Final "Create Property" / "Save Changes" gate: every section must validate
  // — the form fields (property/location/owner/scheduling) AND the checklist
  // (validated via the shared helper, since its items aren't Form.Item fields) —
  // before we submit. On any failure, jump to the offending section so the error
  // is visible rather than saving or leaving the user on a clean-looking tab.
  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      handleFinishFailed(errorInfo); // routes to the first invalid tab + toast
      return;
    }
    const checklistError = validateChecklistItems(questionaire);
    if (checklistError) {
      setActiveTab("checklist");
      message.error(checklistError);
      return;
    }
    form.submit();
  };

  const propertyTab = (
    <>
      <SectionIntro>
        The cover photo, name, room makeup and a short description of the
        property.
      </SectionIntro>

      <Form.Item label="Property Image">
        <Upload
          {...uploadProps}
          accept="image/*"
          beforeUpload={imageOnlyBeforeUpload}
        >
          {fileList.length >= 1 ? null : (
            <div>
              <UploadOutlined />
              <div className="upload-label">Upload Image</div>
            </div>
          )}
        </Upload>
      </Form.Item>

      <Form.Item
        label="Property Name"
        name="propertyName"
        rules={[
          { required: true, message: "Property Name is required" },
          { max: 30, message: "Property name must be max 30 characters" },
        ]}
      >
        <Input maxLength={30} placeholder="e.g. Lakeside Cabin" />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={12} sm={8}>
          <Form.Item
            label="Beds"
            name="beds"
            rules={[{ required: true, message: "Number of beds is required" }]}
          >
            <Select placeholder="Beds">
              {bedOptions.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={8}>
          <Form.Item
            label="Baths"
            name="bath"
            rules={[{ required: true, message: "Number of baths is required" }]}
          >
            <Select placeholder="Baths">
              {bathOptions.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={8}>
          <Form.Item
            label="Kitchens"
            name="kitchen"
            rules={[
              { required: true, message: "Number of kitchens is required" },
            ]}
          >
            <Select placeholder="Kitchens">
              {kitchenOptions.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={8}>
          <Form.Item label="Porches" name="porch">
            <Select placeholder="Porches">
              {(porchOptions || []).map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={12} sm={8}>
          <Form.Item label="Lawns" name="lawn">
            <Select placeholder="Lawns">
              {(lawnOptions || []).map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Property Description"
        name="propertyDescription"
        rules={[{ required: true, message: "Property Description is required" }]}
      >
        <Input.TextArea
          autoSize={{ minRows: 3 }}
          placeholder="Describe the property — type, size, notable features, parking/access notes…"
        />
      </Form.Item>

    </>
  );

  const locationTab = (
    <>
      <SectionIntro>
        Search the address, then drag the pin to fine-tune the exact spot.
      </SectionIntro>

      <Form.Item
        label="Location"
        name="location"
        rules={[{ required: true, message: "Location is required" }]}
      >
        <Input
          ref={(node) => {
            // Ant Design v5 Input exposes the underlying <input> DOM node via
            // `node.input`; the Places Autocomplete attaches to that element.
            locationInputRef.current =
              node?.input ||
              node?.nativeElement?.querySelector?.("input") ||
              null;
          }}
          placeholder="Start typing an address..."
          allowClear
          onChange={() => {
            // A manual edit means the text no longer matches the picked place,
            // so drop the stale map pin. Selecting a suggestion sets the value
            // via the DOM (no React onChange), so this fires only on genuine
            // user typing — `place_changed` re-sets coords after.
            if (coordinates) setCoordinates(null);
          }}
        />
      </Form.Item>

      <Form.Item label="Map Location (drag the marker to fine-tune)">
        {isMapsLoaded ? (
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "320px",
              borderRadius: "8px",
            }}
            center={mapCenter}
            zoom={coordinates ? 16 : 11}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {coordinates && (
              <Marker
                position={coordinates}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        ) : (
          <div
            style={{
              width: "100%",
              height: "320px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
              color: "#999",
            }}
          >
            Loading map...
          </div>
        )}
        {coordinates && (
          <div style={{ marginTop: 8, color: "#888", fontSize: 12 }}>
            Coordinates: {Number(coordinates.lat).toFixed(6)},{" "}
            {Number(coordinates.lng).toFixed(6)}
          </div>
        )}
      </Form.Item>
    </>
  );

  const ownerTab = (
    <>
      <SectionIntro>Who owns the property and how to reach them.</SectionIntro>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Owner Name"
            name="ownerName"
            rules={[
              { required: true, message: "Owner Name is required" },
              {
                pattern: /^[a-zA-Z\s]{1,50}$/,
                message: "Owner Name must be max 50 characters",
              },
            ]}
          >
            <Input maxLength={50} placeholder="e.g. Jane Smith" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Owner Contact"
            name="ownerContact"
            rules={[
              { required: true, message: "Owner Contact is required" },
              {
                pattern: /^(\d{3} \d{3} \d{4}(\s\d{1,5})?)$/,
                message:
                  "Phone number must be in the format 000 000 0000 (up to 15 digits)",
              },
            ]}
          >
            <Input
              maxLength={20}
              placeholder="000 000 0000"
              onChange={(e) => {
                const input = e.target.value.replace(/\D/g, "").substring(0, 15);
                let formattedInput = "";
                if (input.length > 0) formattedInput = input.substring(0, 3);
                if (input.length > 3)
                  formattedInput += ` ${input.substring(3, 6)}`;
                if (input.length > 6)
                  formattedInput += ` ${input.substring(6, 10)}`;
                if (input.length > 10)
                  formattedInput += ` ${input.substring(10, 15)}`;
                form.setFieldsValue({ ownerContact: formattedInput });
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            label="Owner Email"
            name="ownerEmail"
            rules={[
              { required: true, message: "Owner Email is required" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input placeholder="owner@example.com" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const schedulingTab = (
    <>
      <SectionIntro>
        Who inspects this property, how often, and the timezone its deadlines
        follow.
      </SectionIntro>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Assigned inspector(s)"
            name="assignedInspectors"
            help="Any assigned inspector can complete the property's inspection. Leave empty to flag it unassigned on the scheduler."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Select inspector(s)"
            >
              {(usersData || []).map((u) => (
                <Select.Option key={u._id} value={u._id}>
                  {u.name || u.email || u._id}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Inspection frequency"
            name="inspectionIntervalWeeks"
            initialValue={1}
            help="Each cycle starts Monday and is due by the end of its final Sunday (in the property's timezone). Frequency sets how many weeks each cycle spans."
          >
            <Select>
              <Select.Option value={1}>Weekly</Select.Option>
              <Select.Option value={2}>Every 2 weeks</Select.Option>
              <Select.Option value={4}>Every 4 weeks (monthly)</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Property timezone"
            name="timezone"
            initialValue="America/Denver"
            help="Inspection deadlines roll over at the end of the week in this zone."
          >
            <Select showSearch optionFilterProp="children">
              {PROPERTY_TIMEZONES.map((tz) => (
                <Select.Option key={tz.value} value={tz.value}>
                  {tz.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const checklistTab = (
    <>
      <SectionIntro>
        The list of rooms and items inspectors walk through. Optionally start
        from a saved checklist.
      </SectionIntro>

      {templates.length > 0 && (
        <Form.Item
          label="Start from a saved checklist (optional)"
          help="Copies the template's items below. You can edit them for this property without affecting the saved checklist."
        >
          <Select
            placeholder="Select a saved checklist…"
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={(templateId) => onApplyTemplate?.(templateId)}
          >
            {templates.map((t) => (
              <Select.Option key={t._id} value={t._id}>
                {t.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <ChecklistItemsEditor
        questionaire={questionaire}
        setQuestionaire={setQuestionaire}
        handleAddQuestion={handleAddQuestion}
        handleAddProblem={handleAddProblem}
        handleAddResolutionStep={handleAddResolutionStep}
        handleRemoveResolutionStep={handleRemoveResolutionStep}
      />
      {questionaire.length > 0 && onSaveAsTemplate && (
        <Button
          type="link"
          icon={<SaveOutlined />}
          onClick={onSaveAsTemplate}
          style={{ paddingLeft: 0, marginTop: 8 }}
        >
          Save this checklist as a reusable template
        </Button>
      )}
    </>
  );

  // Tab definitions in step order. A completed step (create wizard only) swaps
  // its section icon for a green ✓ so the strip reads as a progress tracker.
  const tabDefs = [
    { key: "property", icon: <HomeOutlined />, text: "Property", content: propertyTab },
    { key: "location", icon: <EnvironmentOutlined />, text: "Location", content: locationTab },
    { key: "owner", icon: <UserOutlined />, text: "Owner", content: ownerTab },
    { key: "scheduling", icon: <ScheduleOutlined />, text: "Scheduling", content: schedulingTab },
    { key: "checklist", icon: <CheckSquareOutlined />, text: "Checklist", content: checklistTab },
  ];

  const tabItems = tabDefs.map(({ key, icon, text, content }) => {
    const done = !isEditing && completedTabs[key];
    return {
      key,
      label: (
        <span>
          {done ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : icon} {text}
        </span>
      ),
      forceRender: true,
      children: content,
    };
  });

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onCancel}
      width={850}
      style={{ top: 20 }}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      // Scroll the body so the action bar (footer) stays pinned and visible no
      // matter how long the active tab is. paddingTop is 0 so the sticky tab
      // strip (see CreateTaskModal.css) sits flush at the top while it scrolls.
      styles={{ body: { maxHeight: "72vh", overflowY: "auto", paddingTop: 0 } }}
      footer={
        isEditing
          ? [
              <Button key="cancel" onClick={onCloseAndReset}>
                Cancel
              </Button>,
              <Button
                key="save"
                type="primary"
                loading={isloading}
                onClick={handleSave}
              >
                {isloading ? "Saving..." : "Save Changes"}
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onCloseAndReset}>
                Cancel
              </Button>,
              currentIndex > 0 && (
                <Button key="back" onClick={handleBack}>
                  Back
                </Button>
              ),
              isLastTab ? (
                <Button
                  key="create"
                  type="primary"
                  loading={isloading}
                  onClick={handleSave}
                >
                  {isloading ? "Saving..." : "Create Property"}
                </Button>
              ) : (
                <Button key="continue" type="primary" onClick={handleContinue}>
                  Continue
                </Button>
              ),
            ].filter(Boolean)
      }
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        onFinish={onFinish}
        onFinishFailed={handleFinishFailed}
      >
        <Tabs
          className="property-modal-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Form>
    </Modal>
  );
};

export default CreateTaskModal;
